/*
  pof-signup: sends the SMS/email signup form to Klaviyo.

  How it works:
  1. Checks the form: at least a phone number or email, and the
     opt-in box must be ticked.
  2. Sends ONLY the channels that were filled in to Klaviyo's public
     "client subscriptions" endpoint. This endpoint is made for websites:
     it uses your PUBLIC Site ID, never a private key.
  3. Shows a success or error message.

  Klaviyo docs: https://developers.klaviyo.com/en/reference/create_client_subscription
*/

if (!customElements.get('pof-signup')) {
  // Klaviyo API version. Klaviyo releases a new one every few months;
  // an old version keeps working for about 2 years.
  const KLAVIYO_REVISION = '2026-10-15';

  class PofSignup extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.message = this.querySelector('.pof-signup__message');
      this.button = this.querySelector('button[type="submit"]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    showMessage(text, type) {
      this.message.textContent = text;
      this.message.dataset.type = type; // used by the CSS for colour
    }

    // Turns "06 12 34 56 78" + "+31" into "+31612345678" (the format Klaviyo needs).
    toInternational(countryCode, number) {
      let digits = number.replace(/[^\d+]/g, '');
      if (digits.startsWith('+')) return digits; // already international
      if (digits.startsWith('00')) return '+' + digits.slice(2);
      digits = digits.replace(/^0+/, ''); // drop the leading 0 used inside a country
      return countryCode + digits;
    }

    async onSubmit(event) {
      event.preventDefault();

      const data = new FormData(this.form);
      const phoneRaw = (data.get('phone') || '').trim();
      const email = (data.get('email') || '').trim();
      const consent = data.get('consent') === 'on';

      // --- 1. Check the form ---
      if (!phoneRaw && !email) {
        return this.showMessage('Please enter your phone number.', 'error');
      }
      if (!consent) {
        return this.showMessage('Please tick the box to join the list.', 'error');
      }

      const phone = phoneRaw ? this.toInternational(data.get('country_code'), phoneRaw) : '';
      if (phone && !/^\+\d{8,15}$/.test(phone)) {
        return this.showMessage('That phone number doesn\'t look right. Please check it.', 'error');
      }
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return this.showMessage('That email address doesn\'t look right. Please check it.', 'error');
      }

      const siteId = this.dataset.siteId;
      const listId = this.dataset.listId;
      if (!siteId || !listId) {
        console.warn('pof-signup: add your Klaviyo Site ID and List ID in Theme settings > SMS & email signup.');
        return this.showMessage(this.dataset.msgError, 'error');
      }

      // --- 2. Build the data for Klaviyo (only the channels that were filled in) ---
      const profile = { subscriptions: {} };
      if (phone) {
        profile.phone_number = phone;
        profile.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
      }
      if (email) {
        profile.email = email;
        profile.subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
      }

      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.dataset.source,
            profile: { data: { type: 'profile', attributes: profile } },
          },
          relationships: { list: { data: { type: 'list', id: listId } } },
        },
      };

      // --- 3. Send it ---
      this.button.disabled = true;
      this.showMessage('', '');

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(siteId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              revision: KLAVIYO_REVISION,
            },
            body: JSON.stringify(body),
          }
        );

        if (!response.ok) throw new Error(`Klaviyo answered ${response.status}`);

        this.form.reset();
        this.showMessage(this.dataset.msgSuccess, 'success');
      } catch (error) {
        console.error('pof-signup:', error);
        this.showMessage(this.dataset.msgError, 'error');
      } finally {
        this.button.disabled = false;
      }
    }
  }

  customElements.define('pof-signup', PofSignup);
}
